'use client';

import Box from '@enlighten/design-system/dist/Box';
import Fade from '@enlighten/design-system/dist/Fade';
import Grid from '@enlighten/design-system/dist/Grid';
import List from '@enlighten/design-system/dist/List';
import ListItem from '@enlighten/design-system/dist/ListItem';
import ListItemButton from '@enlighten/design-system/dist/ListItemButton';
import type { Theme } from '@enlighten/design-system/dist/styles';
import Typography from '@enlighten/design-system/dist/Typography';
import useMediaQuery from '@enlighten/design-system/dist/useMediaQuery';
import Image from 'next/image';
import { useState } from 'react';

import WorkImage1Mobile from '@/assets/images/pages/bizking/cnt_mo_work_1.png';
import WorkImage2Mobile from '@/assets/images/pages/bizking/cnt_mo_work_2.png';
import WorkImage3Mobile from '@/assets/images/pages/bizking/cnt_mo_work_3.png';
import WorkImage1Pc from '@/assets/images/pages/bizking/cnt_pc_work_1.png';
import WorkImage2Pc from '@/assets/images/pages/bizking/cnt_pc_work_2.png';
import WorkImage3Pc from '@/assets/images/pages/bizking/cnt_pc_work_3.png';

const tabs = [
	{
		image: {
			desktop: WorkImage1Pc,
			mobile: WorkImage1Mobile,
		},
		label: '보고서 생성',
		value: 'detect',
		description: `수많은 발전소의 일, 월, 연별 발전량 데이터를 일괄 다운로드하여
반복작업을 멈추고 시간과 비용을 절약할 수 있습니다.`,
	},
	{
		image: {
			desktop: WorkImage2Pc,
			mobile: WorkImage2Mobile,
		},
		label: '이슈 관리',
		value: 'check',
		description: `발전소에서 발생하는 수많은 이슈를 모든 관계자들에게 공유하면
빠르고 정확하게 발전소 관리를 할 수 있습니다.`,
	},
	{
		image: {
			desktop: WorkImage3Pc,
			mobile: WorkImage3Mobile,
		},
		label: '태그 관리',
		value: 'manage',
		description: `특정 발전소를 선택한 후 내가 원하는 대로 발전소 태그를 생성하여
태그별로 발전소를 관리할 수 있습니다.`,
	},
];

export default function Section2() {
	const [tab, setTab] = useState<'detect' | 'check' | 'manage'>('detect');

	const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'));

	return (
		<Grid container justifyContent="center" bgcolor="grey.100" width="100%">
			<Grid container direction={{ xs: 'column', md: 'row' }} width="100%" maxWidth={1440} padding={{ xs: '40px 20px', md: '0 20px' }}>
				<Grid container direction="column" justifyContent="center" flex={{ xs: 'none', md: 1 }}>
					<Typography fontSize={{ xs: 24, md: 40 }} fontWeight={700}>
						{'반복 업무\n발전왕 비즈에선 간편합니다.'}
					</Typography>
					<List
						sx={(theme) => ({
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'flex-start',
							gap: '10px',
							fontSize: { xs: 13, md: 16 },
							fontWeight: 700,
							lineHeight: 1.5,
							color: 'grey.600',
							marginTop: 5,
							padding: 0,
							'.MuiListItem-root': {
								width: 'fit-content',
								padding: 0,
								'.MuiListItemButton-root': {
									bgcolor: 'common.white',
									border: `1px solid ${theme.palette.grey['300']}`,
									borderRadius: 99,
									padding: { xs: '13px 18px', md: '13px 24px' },
									'&.active': {
										color: 'common.white',
										bgcolor: 'common.black',
										borderColor: 'common.black',
									},
								},
							},
						})}
					>
						{tabs.map(({ label, value }, i) => (
							<ListItem key={i}>
								<ListItemButton
									className={tab === value ? 'active' : undefined}
									onClick={() => {
										setTab(value as typeof tab);
									}}
								>
									{label}
								</ListItemButton>
							</ListItem>
						))}
					</List>
					<Grid
						position="relative"
						height={94}
						sx={(theme) => ({
							'.MuiTypography-root': {
								position: 'absolute',
								fontSize: { xs: 14, md: 16 },
								lineHeight: 1.5,
								whiteSpace: { xs: 'normal', md: 'pre-line' },
								color: theme.palette.grey['A400'],
								marginTop: '30px',
								marginBottom: { xs: '14px', md: 2 },
							},
						})}
					>
						{tabs.map(({ value, description }, i) => (
							<Fade key={i} in={tab === value}>
								<Box>
									<Typography
										position="absolute"
										fontSize={{ xs: 14, md: 16 }}
										lineHeight={1.5}
										whiteSpace={{ xs: 'normal', md: 'pre-line' }}
										color="grey.A400"
										marginTop="30px"
										marginBottom={{ xs: '14px', md: 2 }}
									>
										{description}
									</Typography>
								</Box>
							</Fade>
						))}
					</Grid>
				</Grid>
				<Grid position="relative" container flex={1} width="100%">
					<Grid
						position="relative"
						container
						justifyContent="center"
						alignItems="center"
						flex={1}
						width="100%"
						marginTop={{ xs: '20px', md: 0 }}
						sx={{
							aspectRatio: { xs: 4 / 3, md: 1 },
							'& img': {
								position: 'absolute',
								width: '100%',
								height: 'auto',
							},
						}}
					>
						{isMobile ? (
							<>
								{tabs.map(({ image: { mobile: image }, value }, i) => (
									<Fade key={i} in={tab === value}>
										<Image src={image} alt="" />
									</Fade>
								))}
							</>
						) : (
							<>
								{tabs.map(({ image: { desktop: image }, value }, i) => (
									<Fade key={i} in={tab === value}>
										<Image src={image} alt="" />
									</Fade>
								))}
							</>
						)}
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
}
